import type {
    ContentDefinition,
    ForDefinition,
    ParsedDocument,
    PluginDefinition,
    UseBinding,
} from "@magic-md/core";
import { parseMagicMd } from "@magic-md/core";
import { normalizeForDefinitions } from "./for-definition-normalize";

export interface ForScopedUnits {
    definitions: Map<string, { definition: PluginDefinition; ownerForId: string }>;
    contentDefinitions: Map<string, { contentDefinition: ContentDefinition; ownerForId: string }>;
    uses: Array<{ use: UseBinding; ownerForId: string }>;
}

export function collectForScopedUnits(
    forDefinitions: Map<string, ForDefinition> | undefined,
): ForScopedUnits {
    const normalizedForDefinitions = normalizeForDefinitions(forDefinitions);
    const definitions = new Map<string, { definition: PluginDefinition; ownerForId: string }>();
    const contentDefinitions = new Map<string, { contentDefinition: ContentDefinition; ownerForId: string }>();
    const uses: Array<{ use: UseBinding; ownerForId: string }> = [];
    const seenForIds = new Set<string>();
    const seenUseIds = new Set<string>();

    const visit = (forDef: ForDefinition) => {
        if (seenForIds.has(forDef.id)) return;
        seenForIds.add(forDef.id);

        for (const [id, definition] of forDef.innerScope.definitions) {
            if (!definitions.has(id)) {
                definitions.set(id, { definition, ownerForId: forDef.id });
            }
        }

        for (const [id, contentDefinition] of forDef.innerScope.contentDefinitions ?? new Map<string, ContentDefinition>()) {
            if (!contentDefinitions.has(id)) {
                contentDefinitions.set(id, { contentDefinition, ownerForId: forDef.id });
            }
        }

        for (const use of forDef.innerScope.uses ?? []) {
            if (!seenUseIds.has(use.id)) {
                seenUseIds.add(use.id);
                uses.push({ use, ownerForId: forDef.id });
            }
        }

        for (const nestedFor of forDef.innerScope.forDefinitions.values()) {
            visit(nestedFor);
        }
    };

    for (const forDef of normalizedForDefinitions.values()) {
        visit(forDef);
    }

    return { definitions, contentDefinitions, uses };
}

export function collectParsedDocumentUseBindings(
    parsedDocument: ParsedDocument | null | undefined,
): UseBinding[] {
    const uses: UseBinding[] = [
        ...(parsedDocument?.uses ?? []),
        ...collectForScopedUnits(parsedDocument?.forDefinitions).uses.map((entry) => entry.use),
    ];

    // Collect ::use bindings from inside ::content blocks.  Content-inner
    // ::use bindings (e.g. ::use[sqlTotal]{from="libs/pgQueryBase"} inside
    // a ::content[sqlBase] block) must be discoverable so that the config
    // panel can resolve their source config and write patches back.
    if (parsedDocument?.contentDefinitions) {
        for (const contentDef of parsedDocument.contentDefinitions.values()) {
            const inner = parseMagicMd(contentDef.body);
            if (inner.uses && inner.uses.length > 0) {
                uses.push(...inner.uses);
            }
        }
    }

    return uses;
}
